<script>
import { ref, onMounted, onBeforeUnmount } from 'vue';

export default {
	setup() {
		const videoEl = ref(null);
		const expandedVideoEl = ref(null);
		const expanded = ref(false);
		const videoSrc = '/videos/wrap-install-placeholder.mp4';

		onMounted(() => {
			// Belt-and-suspenders for muted autoplay: some browsers don't
			// reliably honor the `muted` HTML attribute set at parse time,
			// so set the property directly before kicking off play().
			const v = videoEl.value;
			if (!v) return;
			v.muted = true;
			v.play().catch(() => {});
		});

		const open = () => {
			expanded.value = true;
			videoEl.value?.pause();
			document.documentElement.classList.add('overflow-y-hidden');
		};

		const close = () => {
			expanded.value = false;
			document.documentElement.classList.remove('overflow-y-hidden');
			videoEl.value?.play().catch(() => {});
		};

		const onExpandedMounted = (el) => {
			expandedVideoEl.value = el;
			if (el) el.play().catch(() => {});
		};

		const onKeyup = (event) => {
			if (expanded.value && event.key === 'Escape') close();
		};

		onMounted(() => window.addEventListener('keyup', onKeyup));
		onBeforeUnmount(() => {
			window.removeEventListener('keyup', onKeyup);
			document.documentElement.classList.remove('overflow-y-hidden');
		});

		return { videoEl, expanded, videoSrc, open, close, onExpandedMounted };
	},
};
</script>

<template>
	<div class="mt-10 sm:mt-20">
		<div class="text-center mb-8 sm:mb-10">
			<p
				class="font-display uppercase tracking-wide text-2xl sm:text-3xl font-bold text-primary-light mb-2"
			>
				See Us In Action
			</p>
		</div>

		<button
			type="button"
			@click="open"
			aria-label="Expand video"
			class="group relative block w-full max-w-3xl sm:max-w-4xl mx-auto clip-corner shadow-glow-lg border border-ternary-dark overflow-hidden cursor-pointer"
		>
			<video
				ref="videoEl"
				:src="videoSrc"
				autoplay
				muted
				loop
				playsinline
				class="w-full h-auto object-cover block"
			></video>

			<!-- Expand affordance -->
			<div
				class="absolute inset-0 flex items-center justify-center bg-black bg-opacity-0 group-hover:bg-opacity-30 duration-300"
			>
				<div
					class="p-4 clip-corner-sm bg-secondary-dark bg-opacity-0 group-hover:bg-opacity-80 border border-ternary-dark opacity-0 group-hover:opacity-100 duration-300"
				>
					<svg
						xmlns="http://www.w3.org/2000/svg"
						viewBox="0 0 24 24"
						fill="none"
						stroke="currentColor"
						stroke-width="2"
						stroke-linecap="round"
						stroke-linejoin="round"
						class="w-8 h-8 text-primary-light"
					>
						<polyline points="15 3 21 3 21 9"></polyline>
						<polyline points="9 21 3 21 3 15"></polyline>
						<line x1="21" y1="3" x2="14" y2="10"></line>
						<line x1="3" y1="21" x2="10" y2="14"></line>
					</svg>
				</div>
			</div>
		</button>

		<!-- Expanded video modal -->
		<Teleport to="body">
			<transition name="lightbox-fade">
				<div
					v-if="expanded"
					@click="close"
					class="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-90 p-4 sm:p-10"
				>
					<button
						@click.stop="close"
						class="absolute top-5 right-5 text-primary-light hover:text-brand-500 duration-300"
						aria-label="Close"
					>
						<svg
							xmlns="http://www.w3.org/2000/svg"
							viewBox="0 0 24 24"
							fill="none"
							stroke="currentColor"
							stroke-width="2"
							stroke-linecap="round"
							stroke-linejoin="round"
							class="w-8 h-8"
						>
							<line x1="18" y1="6" x2="6" y2="18"></line>
							<line x1="6" y1="6" x2="18" y2="18"></line>
						</svg>
					</button>

					<div class="max-w-5xl max-h-full w-full" @click.stop>
						<video
							:ref="onExpandedMounted"
							:src="videoSrc"
							controls
							loop
							playsinline
							class="w-full max-h-[80vh] object-contain clip-corner border border-ternary-dark"
						></video>
					</div>
				</div>
			</transition>
		</Teleport>
	</div>
</template>

<style scoped>
.lightbox-fade-enter-active {
	transition: opacity 0.4s cubic-bezier(0.16, 1, 0.3, 1),
		transform 0.4s cubic-bezier(0.16, 1, 0.3, 1);
}
.lightbox-fade-leave-active {
	transition: opacity 0.3s cubic-bezier(0.4, 0, 1, 1),
		transform 0.3s cubic-bezier(0.4, 0, 1, 1);
}
.lightbox-fade-enter-from,
.lightbox-fade-leave-to {
	opacity: 0;
	transform: scale(0.94);
}
</style>
